import { BaseAPI, PagingOpts } from 'lib/api/base/base-api';
import { State } from 'lib/models/diagnoses/choices/state';
import { SymptomType } from 'lib/models/diagnoses/choices/symptom-type';

import { SymptomLike } from 'lib/models/diagnoses/symptom';
import { DatabaseStatistics } from 'lib/models/statistics/database.statistics';
import { Page } from 'lib/models/utils/page';

export type SearchOpts = {
  q?: string;
  type?: SymptomType;
  state?: State;
};

/**
 * The public API for symptoms.
 *
 * It will list all symptoms, but you can only load published symptoms.
 */
export class SymptomsAPI extends BaseAPI {
  static keys = {
    all: (): (string | number | boolean | undefined | null)[] => {
      return ['public', 'symptoms'];
    },
    list: (
      opts: SearchOpts & PagingOpts
    ): (string | number | boolean | undefined | null)[] => {
      return [
        'public',
        'symptoms',
        'list',
        opts.q,
        opts.type?.key,
        opts.state?.tag,
        opts.page,
        opts.pageSize
      ];
    },
    get: (id: number): (string | number | boolean | undefined | null)[] => {
      return ['public', 'symptoms', id];
    },
    statistics: (
      opts: SearchOpts
    ): (string | number | boolean | undefined | null)[] => {
      return [
        'public',
        'symptoms',
        'statistics',
        opts.q,
        opts.type?.key,
        opts.state?.tag
      ];
    }
  };

  /**
   * List symptoms with search options.
   *
   * @param opts Search filters and pagination
   * @returns A promise of a page of SymptomLike instances
   */
  static async list({
    q,
    type,
    state,
    page,
    pageSize
  }: SearchOpts & PagingOpts): Promise<Page<SymptomLike>> {
    // Perform the API request
    const response = await this._get('/symptoms/', {
      args: {
        q,
        kind: type === SymptomType.NOT_SET ? undefined : type?.key,
        state: state?.tag,
        page,
        'page-size': pageSize
      }
    });

    console.log('Response from /symptoms/: ', response);

    // Parse response
    const json = await response.json();

    console.log('JSON from /symptoms/: ', json);
    const items = json.items.map((result: any) =>
      SymptomLike.fromJson(result)
    ) as SymptomLike[];

    console.log('Items from /symptoms/: ', items);

    return Page.fromJson(json, items);
  }

  /**
   * Get a specific symptom.
   *
   * @param id The ID of the symptom
   * @returns A promise of a SymptomLike instance
   */
  static async get(id: number): Promise<SymptomLike> {
    // Perform the API request
    const response = await this._get('/symptoms/' + id + '/');

    // Parse response
    const json = await response.json();
    const symptom = SymptomLike.fromJson(json);

    return symptom;
  }

  /**
   * Get statistics about diagnoses in the database.
   *
   * TODO Not in use. Should this have been moved to moderator API?
   *
   * @returns A promise of a statistics object
   */
  static async statistics({
    q,
    type,
    state
  }: SearchOpts = {}): Promise<DatabaseStatistics> {
    // Perform the API request
    const response = await this._get('/symptoms/statistics/', {
      args: {
        q,
        kind: type === SymptomType.NOT_SET ? undefined : type?.key,
        state: state?.tag
      }
    });
    // Parse the response
    const json = await response.json();
    // Return the parsed response
    const statistics = new DatabaseStatistics();
    statistics.published = json.published;
    statistics.drafts = json.drafts;
    statistics.stubs = json.stubs;
    return statistics;
  }
}
